import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertTriggers,
  convertQuestionMappings,
  convertNotRequired,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = null
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 名單式調查參數
  const { sfdSamplerId, sfdPId } = useSurveyParams()

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // JSON 設定說明（code review 已確認）：
  // - q25：隨機池含全部 10 個一般選項（含第 5 項「產業研究人才」），「其他」固定最後
  // - q31：「尚未使用」「不適用（非從事公關實務工作）」刻意不設互斥、不入隨機池，
  //   固定在一般選項之後、「其他」之前（刻意設計，非漏設）
  const visibleIfConditionsInit = null
  // 段落標題假顯示題 q1/q7/q15/q24/q29/q38/q41、開放題 q42~q44 非必填
  const notRequired = convertNotRequired([1, 7, 15, 24, 29, 38, 41, 42, 43, 44])
  const mappedRateChoice = null
  const pageBreakQuestions = null
  // q2 選 7（非主管職級的公關／傳播從業人員）→ 直接結束，與正常完成同一結尾頁
  const triggersConditionsInit = convertTriggers([
    { type: 'complete', expression: `{q2} = 7` },
  ])
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 結尾頁（含 q2=7 提前結束者，同一份）；推薦卡片 flex-wrap，窄螢幕圖文上下堆疊
  const endSurveyMsg = () => {
    setCompletionMessage(
      `<div class="figma-frame" data-frame-name="問卷結尾頁" style="font-family:Inter,'Noto Sans TC','PingFang TC','Microsoft JhengHei',sans-serif;display:flex;flex-direction:column;gap:16px;width:100%;box-sizing:border-box;"><div style="display:flex;flex-direction:column;align-items:center;gap:12px;padding:16px 0 4px 0;"><div style="font-size:26px;line-height:1.4;font-weight:900;color:#1A1A1A;text-align:center;">您已完成並成功提交問卷</div><div style="width:100%;height:2px;background:#19B394;border-radius:1px;"></div></div><div style="display:flex;flex-direction:column;background:#FFFFFF;border-radius:16px;padding:20px 16px;border:1px solid #E8E8E8;"><div style="font-size:15px;line-height:1.9;color:#1A1A1A;font-weight:400;">感謝您的填寫，讓台灣公關產業擁有自己的數據與話語權。</div><div style="height:16px;"></div><div style="font-size:15px;line-height:1.9;color:#1A1A1A;font-weight:400;">台灣暨臺北市國際公共關係協會</div></div><div style="display:flex;flex-direction:column;gap:14px;width:100%;box-sizing:border-box;background:#FFFFFF;border-radius:16px;padding:20px 16px;border:1px solid #E8E8E8;"><div style="display:flex;flex-direction:column;gap:10px;"><div style="font-size:12px;line-height:1.6;font-weight:700;color:#0B7A64;">合作夥伴：DSI典通公司</div><div style="display:flex;align-items:center;gap:8px;"><div style="flex:none;width:5px;height:22px;background:#19B394;border-radius:3px;"></div><div style="font-size:18px;line-height:1.5;font-weight:900;color:#1A1A1A;">從日常選擇，看見人們生活與消費的變化</div></div><div style="font-size:13px;line-height:1.85;color:#1A1A1A;font-weight:400;">典通 DSIGroup 結合專業市場研究、問卷調查平台與 EZChoice 會員社群，串連問卷設計、民眾參與、數據分析與洞察應用，打造持續累積的調研服務生態系。</div><div style="font-size:13px;line-height:1.85;color:#1A1A1A;font-weight:400;">我們相信，每個日常選擇背後，都有值得理解的心理、需求與生活情境。透過輕鬆參與的調查，讓數據不只是數字，更能幫助我們理解人們如何生活、如何選擇，以及社會與消費趨勢如何改變。</div><div style="display:flex;flex-direction:column;gap:8px;background:#FAFAF8;border-radius:12px;padding:12px;"><div style="display:flex;align-items:center;gap:8px;"><div style="width:4px;height:16px;background:#19B394;border-radius:2px;"></div><div style="font-size:14px;line-height:1.5;font-weight:700;color:#1A1A1A;">認識 EZChoice｜探索生活，理解選擇</div></div><div style="font-size:13px;line-height:1.85;color:#1A1A1A;font-weight:400;">想知道更多活動資訊，請追蹤關注 <a href="https://www.facebook.com/EasyCTRs/" style="color:#0B7A64;text-decoration:underline;">【輕鬆點點　抽大獎】粉絲專頁</a></div><div style="display:flex;flex-wrap:wrap;gap:24px;align-items:center;justify-content:flex-start;padding:4px 0;"><a href="https://www.facebook.com/EasyCTRs/" style="display:block;text-decoration:none;"><img src="https://jinny-tsai.github.io/EZChoiceAdsImage/Facebook_Logo_Primary.png" alt="Facebook" style="display:block;height:28px;width:auto;"></a><a href="https://www.instagram.com/ezchoice.tw/" style="display:block;text-decoration:none;"><img src="https://jinny-tsai.github.io/EZChoiceAdsImage/Instagram_Glyph_Gradient.png" alt="Instagram" style="display:block;height:28px;width:auto;"></a><a href="https://www.threads.com/@ezchoice.tw" style="display:block;text-decoration:none;"><img src="https://jinny-tsai.github.io/EZChoiceAdsImage/threads-logo-black.png" alt="Threads" style="display:block;height:28px;width:auto;"></a></div></div></div><div style="height:1px;background:#E8E8E8;"></div><div style="display:flex;flex-direction:column;gap:2px;"><div style="display:flex;align-items:center;gap:8px;"><div style="width:5px;height:22px;background:#19B394;border-radius:3px;"></div><div style="font-size:22px;line-height:1.4;font-weight:900;color:#1A1A1A;">推薦活動</div></div><div style="font-size:13px;line-height:1.7;color:#555555;padding-left:13px;">現在有這些活動正在進行中，歡迎一起來玩！</div></div><div style="height:1px;background:#E8E8E8;"></div><div style="display:flex;flex-wrap:wrap;gap:12px;align-items:flex-start;"><a href="https://ezcweb.dsigroup.com.tw/P261012103925422" style="flex:1 1 132px;max-width:240px;display:block;"><img src="https://jinny-tsai.github.io/EZChoiceAdsImage/2610/web-survey-cover-square1-4-5.jpg" alt="真心喜歡，還是捨不得點數？會員權益與消費心理大調查" style="display:block;width:100%;height:auto;box-sizing:border-box;border-radius:8px;border:1px solid #E8E8E8;"></a><div style="flex:999 1 200px;min-width:0;display:flex;flex-direction:column;gap:6px;"><div style="font-size:15px;line-height:1.5;font-weight:700;color:#1A1A1A;">填問卷抽 <span style="color:#0B7A64;">LINE Point 50 點</span></div><div style="display:flex;gap:6px;align-items:flex-start;"><div style="display:inline-flex;flex:none;width:62px;justify-content:center;box-sizing:border-box;background:#E6F7F3;color:#0B7A64;font-size:11px;font-weight:700;padding:1px 0;border-radius:999px;line-height:1.7;">抽獎</div><div style="flex:1;font-size:13px;line-height:1.7;color:#1A1A1A;">完整填答並留下手機號碼，抽出 100 名</div></div><div style="display:flex;gap:6px;align-items:flex-start;"><div style="display:inline-flex;flex:none;width:62px;justify-content:center;box-sizing:border-box;background:#FFF3DC;color:#B45309;font-size:11px;font-weight:700;padding:1px 0;border-radius:999px;line-height:1.7;">APP 加碼</div><div style="flex:1;font-size:13px;line-height:1.7;color:#1A1A1A;">2026/10/14 起註冊的新會員，完成指定任務可再加贈 <span style="color:#B45309;font-weight:700;">LINE Point 80 點</span>（前 1,000 名）</div></div><a href="https://ezcweb.dsigroup.com.tw/P261012103925422" style="display:block;text-align:center;background:#19B394;color:#FFFFFF;font-size:14px;font-weight:700;line-height:1.5;padding:8px 12px;border-radius:10px;text-decoration:none;">立即填答</a><div style="font-size:11px;line-height:1.7;color:#78716C;">活動至 2026/10/30 止，限 18 歲以上，每人限答一份</div></div></div><div style="height:1px;background:#E8E8E8;"></div><div style="display:flex;flex-direction:column;gap:10px;"><div style="display:flex;flex-wrap:wrap;gap:12px;align-items:flex-start;"><a href="https://www.facebook.com/share/p/1JJYBfpAau/" style="flex:1 1 132px;max-width:240px;display:block;"><img src="https://jinny-tsai.github.io/EZChoiceAdsImage/2610/prize-preview.jpg" alt="終極狂歡 豪華大獎：Nintendo 任天堂 Switch 2 主機、SONGEN 松井 3D 擬真火焰歐風電暖壁爐、Marshall Willen II 攜帶式藍牙喇叭、Nespresso 膠囊咖啡機 Essenza Mini、LEGO 樂高 Iconic 節慶薑餅屋、JWAY 全球鍋 國際電壓旅行快煮鍋" style="display:block;width:100%;height:auto;box-sizing:border-box;border-radius:8px;border:1px solid #E8E8E8;"></a><div style="flex:999 1 200px;min-width:0;display:flex;flex-direction:column;gap:6px;"><div style="font-size:15px;line-height:1.5;font-weight:700;color:#1A1A1A;">2026 年終大獎預告</div><div style="font-size:13px;line-height:1.7;color:#1A1A1A;">💎 <span style="color:#B45309;font-weight:700;">用「會員虛擬鑽石」就能參加年終大獎抽獎！</span><br>各獎項兌換條件不同，詳情請至 APP 內的【禮物】查看。※</div></div></div><div style="font-size:11px;line-height:1.7;color:#78716C;">※ 什麼是會員虛擬鑽石？這是 EZChoice APP 提供給會員的虛擬鑽石，可用來參加 APP 內的會員限定抽獎活動。</div><div style="display:flex;flex-direction:column;gap:8px;background:#FFF3DC;border:1.5px solid #F5A623;border-radius:12px;padding:12px 14px;"><div style="font-size:14px;line-height:1.6;font-weight:700;color:#B45309;">下載 EZChoice 會員 APP，領鑽石、抽大獎</div><div style="display:flex;flex-direction:column;gap:8px;align-items:center;"><img src="https://jinny-tsai.github.io/EZChoiceAdsImage/EZChoice_download_bwcode.png" alt="下載 EZChoice APP 的 QR Code" style="flex:none;display:block;width:150px;height:auto;max-width:100%;box-sizing:border-box;background:#FFFFFF;border-radius:8px;padding:4px;"><div style="font-size:12px;line-height:1.7;color:#555555;text-align:center;">用電腦填答？請用手機掃描 QR Code 下載 APP。<br>想參加會員專屬抽獎，下載後請點【註冊新帳號】完成註冊，才能保障您的領獎資格哦！</div></div><a href="https://ezc-appweb.dsigroup.com.tw/app.html" style="display:block;text-align:center;background:#F5A623;color:#FFFFFF;font-size:15px;font-weight:700;line-height:1.5;padding:10px 12px;border-radius:10px;text-decoration:none;">下載 EZChoice APP</a></div></div></div><div style="font-size:18px;line-height:1.6;font-weight:700;color:#1A1A1A;text-align:center;padding:8px 0 4px 0;">請按【完成】前往下載 EZChoice<br>或填寫更多問卷</div></div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    userIdRef,
    enableProgressSaving: webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
